import { clearAuthUser, refreshAuthUser } from '@/src/redux/slices/authSlice'
import { deleteCookie } from '@/src/utils/browser'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic'
import { Box, Divider, Link, List, ListItem, ListItemText, Popover, SvgIcon, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useLocale } from 'next-intl'
import React from 'react'
import BrandLogo from '../brandLogo'
import LanguageCurrencyDropdownLogic from '../LanguageCurrencyDropdown'
import { useRouter } from '@/src/i18n/navigation'
import { useLazyUserLogoutQuery } from '@/src/api/rtkQuery/userApi'

interface HeaderProps {
    navLinks: { label: string; href: string; labelKey?: string }[]
    options: {
        language: { code: string; label: string; Flag: React.ComponentType<any> }[]
        currency: { code: string; label: string; Flag: React.ComponentType<any>; symbol: string }[]
    }
    selected: {
        language: { code: string; label: string; Flag: React.ComponentType<any> }
        currency: { code: string; label: string; Flag: React.ComponentType<any>; symbol: string }
    }
    menuAnchors: {
        user: null | HTMLElement
        language: null | HTMLElement
        currency: null | HTMLElement
    }
    onMenuOpen: (menu: 'user' | 'language' | 'currency', event: React.MouseEvent<HTMLElement>) => void
    onMenuClose: (menu: 'user' | 'language' | 'currency') => void
    onSelect: (type: 'language' | 'currency', value: any) => void
    modals: { signIn: boolean }
    onModalOpen: (modal: 'signIn') => void
    onModalClose: (modal: 'signIn') => void
    t: (key: string) => string
    tCurrency: (key: string) => string
    user: { signedIn: boolean; email?: string; displayName?: string; mobile?: string; countryCode?: string }
    dispatch: any
    showLangCurrDropdown: boolean
    showDeals?: boolean
    isOffersPage?: boolean
    hideSignIn?: boolean
}

const Header: React.FC<HeaderProps> = ({
    navLinks,
    options,
    selected,
    menuAnchors,
    onMenuOpen,
    onMenuClose,
    onSelect,
    modals,
    onModalOpen,
    onModalClose,
    t,
    tCurrency,
    user,
    dispatch,
    showLangCurrDropdown,
    showDeals = true,
    isOffersPage = false,
    hideSignIn = false
}) => {
    const theme = useTheme()
    const locale = useLocale()
    const supportHref = `/contactUs.${locale}.html`
    const router = useRouter()
    // Helper to clear cookies and sign out
    const [triggerLogout] = useLazyUserLogoutQuery()

    const handleSignOut = async () => {
        try {
            // Call logout API
            await triggerLogout()?.unwrap()

            // Clear redux state
            dispatch(clearAuthUser())

            // Remove cookies
            const domain = '.' + window.location.hostname
            deleteCookie('usermisc', domain)
            deleteCookie('userid', domain)
            deleteCookie('userData', domain)

            // Refresh user state
            dispatch(refreshAuthUser())
        } catch (error) {
            console.error('Logout failed:', error)
        }
    }
    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pl: { xs: 2, sm: 2.5 },
                    py: { xs: 1.5, sm: 2 },
                    pr: { xs: 2, sm: 2.5, md: 6 },
                    height: { xs: 48, sm: 54 }
                }}
            >
                {/* Left: Logo + Nav */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 3 } }}>
                    <Box
                        sx={{
                            height: { xs: 22, sm: 26 },
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer'
                        }}
                        onClick={() => (window.location.href = '/')}
                    >
                        <BrandLogo />
                    </Box>
                    <Box component="nav" sx={{ display: 'flex', gap: 2.5 }}>
                        {navLinks.map((link, index) => (
                            <Link
                                key={link.label}
                                href={link.href}
                                underline="none"
                                sx={{
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: '#1a2a3a',
                                    '&:hover': { color: '#194E91' },
                                    transition: 'color 0.2s',
                                    px: 0.7,
                                    display: {
                                        xs: index < 2 ? 'block' : 'none',
                                        sm: index < 2 ? 'block' : 'none',
                                        md: 'block'
                                    }
                                }}
                            >
                                {t(
                                    link.labelKey ||
                                        link.label
                                            .replace(/ \+ /g, 'Plus')
                                            .replace(/ /g, '')
                                            .replace(/^[A-Z]/, c => c.toLowerCase())
                                )}
                            </Link>
                        ))}
                    </Box>
                </Box>
                {/* Right: Actions */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1.5, sm: 2.5 },
                        fontSize: { xs: 13, sm: 14 },
                        fontWeight: 500,
                        color: '#1a2a3a'
                    }}
                >
                    {/* Deals: visible only on desired pages */}
                    {showDeals && (
                        <Box
                            sx={{
                                position: 'relative',
                                display: { xs: 'none', sm: 'none', md: 'flex' },
                                alignItems: 'center',
                                cursor: 'pointer',
                                marginRight: '6px'
                            }}
                            onClick={() => router.push('/offers')}
                        >
                            <Box sx={{ mr: 0.7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <SvgIcon sx={{ width: 17, height: 17 }}>
                                    <svg width="17" height="17" fill="none" viewBox="0 0 24 24">
                                        <path
                                            d="M4 7V6a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v1"
                                            stroke="#1a2a3a"
                                            strokeWidth="1.3"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        ></path>
                                        <rect
                                            width="20"
                                            height="13"
                                            x="2"
                                            y="7"
                                            rx="2"
                                            stroke="#1a2a3a"
                                            strokeWidth="1.3"
                                        ></rect>
                                        <circle cx="17" cy="12" r="1" fill="#1a2a3a"></circle>
                                    </svg>
                                </SvgIcon>
                            </Box>
                            <span style={{ fontSize: 14 }}>{t('deals')}</span>
                        </Box>
                    )}
                    {/* Language/Currency Dropdown Button - Hidden on mobile */}
                    {showLangCurrDropdown && (
                        <Box sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}>
                            <LanguageCurrencyDropdownLogic
                                selectedLanguage={selected.language}
                                selectedCurrency={selected.currency}
                                languageOptions={options.language}
                                currencyOptions={options.currency}
                                onLanguageSelect={lang => onSelect('language', lang)}
                                onCurrencySelect={curr => onSelect('currency', curr)}
                                t={t}
                                tCurrency={tCurrency}
                                isOffersPage={isOffersPage}
                            />
                        </Box>
                    )}
                    {/* Support Link (replaces WhatsApp) */}
                    <Link
                        href={supportHref}
                        underline="none"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0.5, sm: 0.7 },
                            color: '#1a2a3a',
                            textDecoration: 'none',
                            '&:hover': {
                                color: '#194E91'
                            }
                        }}
                    >
                        <HeadsetMicIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                        <span style={{ fontSize: 14 }}>{t('support') || 'Support'}</span>
                    </Link>
                    {user.signedIn ? (
                        <Box
                            sx={{
                                display: { xs: 'none', sm: 'none', md: 'flex' },
                                alignItems: 'center',
                                cursor: 'pointer',
                                ml: 1,
                                pointerEvents: user.displayName || user.email ? 'auto' : 'none',
                                opacity: user.displayName || user.email ? 1 : 0.7
                            }}
                            onClick={e => (user.displayName || user.email) && onMenuOpen('user', e)}
                        >
                            <PersonOutlineIcon sx={{ fontWeight: 300, fontSize: 20, mr: 0.5 }} />
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                    fontSize: 15,
                                    maxWidth: 120,
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {user.displayName || user.email?.split('@')[0] || 'User'}
                            </Typography>
                            {(user.displayName || user.email) && (
                                <SvgIcon sx={{ fontSize: 17, ml: 0.5 }}>
                                    <svg width="17" height="17" viewBox="0 0 24 24">
                                        <path d="M7 10l5 5 5-5" stroke="#1a2a3a" strokeWidth="1.3" fill="none" />
                                    </svg>
                                </SvgIcon>
                            )}
                        </Box>
                    ) : !hideSignIn ? (
                        <Link
                            href="#"
                            underline="none"
                            sx={{
                                display: { xs: 'none', sm: 'none', md: 'inline' },
                                color: '#1a2a3a',
                                fontWeight: 500,
                                fontSize: 14,
                                ml: 0.7,
                                cursor: 'pointer',
                                '&:hover': { color: '#194E91' }
                            }}
                            onClick={e => {
                                e.preventDefault()
                                onModalOpen('signIn')
                            }}
                        >
                            {t('signIn')}
                        </Link>
                    ) : null}
                    <Popover
                        open={!!menuAnchors.user && user.signedIn}
                        anchorEl={menuAnchors.user}
                        onClose={() => onMenuClose('user')}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        PaperProps={{
                            sx: {
                                mt: 1,
                                borderRadius: 1.5,
                                minWidth: 185,
                                boxShadow: 3,
                                p: 0,
                                overflow: 'hidden',
                                background: '#fff'
                            }
                        }}
                    >
                        <List sx={{ p: 0, maxHeight: 300, overflowY: 'auto' }}>
                            {[
                                { label: t('userMenu.myBookings'), href: '/accounts/trips' },
                                { label: t('userMenu.myWallet'), href: '/accounts/wallet' },
                                { label: t('userMenu.myReviews'), href: '/manageReviews' },
                                { label: t('userMenu.giveFeedback'), href: '/feedback' }
                            ].map(item => (
                                <ListItem
                                    key={item.label}
                                    component="a"
                                    href={item.href}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.8,
                                        py: 0.75,
                                        px: 2,
                                        minHeight: 40,
                                        border: 0,
                                        borderRadius: 0,
                                        fontWeight: 500,
                                        fontSize: 15,
                                        color: '#1a2a3a',
                                        background: '#fff',
                                        textDecoration: 'none',
                                        transition: 'background 0.2s',
                                        '&:hover': {
                                            background: '#f3f4f6',
                                            textDecoration: 'none'
                                        }
                                    }}
                                >
                                    <ListItemText
                                        primary={item.label}
                                        primaryTypographyProps={{
                                            fontSize: 15,
                                            fontWeight: 500,
                                            color: '#4b5563'
                                        }}
                                    />
                                </ListItem>
                            ))}
                            <Divider />
                            <ListItem
                                component="button"
                                onClick={handleSignOut}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.8,
                                    py: 0.5,
                                    px: 2,
                                    minheight: 25,
                                    border: 0,
                                    borderRadius: 0,
                                    fontWeight: 500,
                                    fontSize: 15,
                                    color: 'error.main',
                                    background: '#fff',
                                    transition: 'background 0.2s',
                                    '&:hover': {
                                        background: '#f3f4f6'
                                    }
                                }}
                            >
                                <ListItemText
                                    primary={t('userMenu.signOut')}
                                    primaryTypographyProps={{ fontSize: 15, fontWeight: 500, color: 'error.main' }}
                                />
                            </ListItem>
                        </List>
                    </Popover>
                </Box>
            </Box>
        </>
    )
}

export { Header }
export type { HeaderProps }
